import React, { useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import EcoCreditsManager from "../../../services/home/EcoCreditsManager";
import AddAddressModal from "../addressComponents/AddAddressModal";
import UserTestimonials from "../userProfileComponents/UserTestimonials";
import {
  Mail,
  Phone,
  ShieldCheck,
  Globe,
  BadgeCheck,
  ShoppingCart,
  Heart,
  Package,
  BookOpen,
  LogOut,
  CreditCard,
  Pen,
  MapPin,
} from "lucide-react";
import { motion } from "framer-motion";
import { resetCart } from "../../../features/cartSlice";
import { logout, updateUser } from "../../../features/authSlice";
import { persistor } from "../../../app/store";
import { resetSubscription } from "../../../features/subscriptionSlice";
import UploadPhotoModal from "./UploadPhotoModal";
import AuthManager from "../../../services/dashboard/AuthManager";
import config from "../../../config";
import { FaWhatsapp, FaInstagram, FaShareAlt } from "react-icons/fa";
import { FaXTwitter } from "react-icons/fa6";
import { useEffect } from "react";
import { Award, Gift, Leaf } from "lucide-react";

const UserProfile = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const token = useSelector((s) => s.auth.token);
  const user = useSelector((s) => s.auth.user) || {};
  const [isRedeeming, setIsRedeeming] = useState(false);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [couponModal, setCouponModal] = useState({
    visible: false,
    code: "",
    expiry: "",
  });
  const [isAddressModalOpen, setIsAddressModalOpen] = useState(false);
  const [editingAddress, setEditingAddress] = useState(null); // for edit
  const [isPlanting, setIsPlanting] = useState(false);

  const handleLogout = () => {
    persistor.purge();
    dispatch(resetCart());
    dispatch(resetSubscription());
    dispatch(logout());
    navigate("/login");
  };

  const handleUpload = async (file) => {
    try {
      await AuthManager.uploadProfilePhoto(user.userId, file, token);
      const res = await AuthManager.getUserDetails(user.userId, token);
      dispatch(updateUser(res.responseData));
      setIsModalOpen(false);
      toast.success("Profile photo updated");
    } catch {
      toast.error("Upload failed");
    }
  };

  // Eco credits
  const eco = user.ecoCredits || 0;
  const trees = Math.floor(eco / 50);
  const next = (trees + 1) * 500 - eco;

  useEffect(() => {
    const refreshUser = async () => {
      try {
        const res = await AuthManager.getUserDetails(user.userId, token);
        dispatch(updateUser(res.responseData));
      } catch (err) {
        console.error("Failed to refresh user", err);
      }
    };
    refreshUser();
  }, []);

  const handleRedeemEcoCredits = async () => {
    if (eco < 500) {
      toast.error("You need at least 500 eco credits to redeem.");
      return;
    }

    try {
      setIsRedeeming(true);

      const coupon = await EcoCreditsManager.redeemEcoCredits(user.userId);

      toast.success(`Coupon ${coupon.couponCode} created!`);

      setCouponModal({
        visible: true,
        code: coupon.couponCode,
        expiry: parseDateFromArray(coupon.expiryDate).toLocaleDateString(),
      });

      const updatedUser = await AuthManager.getUserDetails(user.userId, token);
      dispatch(updateUser(updatedUser.responseData));
    } catch (err) {
      toast.error(err.message || "Redemption failed.");
    } finally {
      setIsRedeeming(false);
    }
  };
  const handlePlantTree = async () => {
    if (eco < 500) {
      toast.error("You need at least 500 eco credits to plant a tree.");
      return;
    }

    try {
      setIsPlanting(true);

      const result = await EcoCreditsManager.plantTree(user.userId, 500, "Planting via user profile");

      toast.success("A tree has been planted on your behalf! 🌳");

      const updatedUser = await AuthManager.getUserDetails(user.userId, token);
      dispatch(updateUser(updatedUser.responseData));
    } catch (err) {
      toast.error(err.message || "Tree planting failed.");
    } finally {
      setIsPlanting(false);
    }
  };


  const parseDateFromArray = (arr) => {
    const [year, month, day, hour, min, sec] = arr;
    return new Date(year, month - 1, day, hour, min, sec); // month is 0-based
  };
  const nextMilestone = eco < 500 ? 500 : Math.ceil((eco + 1) / 500) * 500;

  return (
    <motion.div
      className="max-w-4xl mx-auto p-6 space-y-8"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
    >
      {/* Profile Header */}
      <div className="relative bg-gradient-to-r from-primary-shade2 to-primary-shade4 rounded-2xl p-6 flex flex-col md:flex-row items-center md:items-start gap-6 shadow-lg">
        <div className="relative">
          <img
            src={
              user.profilePhotoUrl
                ? `${config.url}/userImages/${user.profilePhotoUrl}`
                : "https://ui-avatars.com/api/?size=128&name=" +
                  encodeURIComponent(user.firstName + " " + user.lastName)
            }
            alt="Profile"
            className="w-28 h-28 rounded-full border-4 border-white object-cover"
          />
          <button
            onClick={() => setIsModalOpen(true)}
            className="absolute bottom-0 right-0 bg-white rounded-full p-1 shadow-md hover:bg-primary transition"
            title="Edit photo"
          >
            <Pen className="text-primary w-4 h-4" />
          </button>
        </div>
        <div className="flex-1 text-center md:text-left">
          <h1 className="text-2xl font-bold text-white">
            {user.firstName} {user.lastName}
          </h1>
          <p className="text-white/80 mt-1">ID: #{user.userId}</p>
          <div className="mt-3 flex flex-wrap justify-center md:justify-start gap-3">
            <IconAction
              icon={<CreditCard size={18} />}
              label="Subscriptions"
              onClick={() => navigate("/subscription-details")}
            />
            <IconAction
              icon={<ShoppingCart size={18} />}
              label="Cart"
              onClick={() => navigate("/Cart")}
            />
            <IconAction
              icon={<Heart size={18} />}
              label="Wishlist"
              onClick={() => navigate("/Wishlist")}
            />
            <IconAction
              icon={<Package size={18} />}
              label="My Orders"
              onClick={() => navigate("/myOrders")}
            />
          </div>
        </div>
      </div>

      {/* Details Card */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {[
          { icon: <Mail />, label: "Email", value: user.emailId },
          { icon: <Phone />, label: "Mobile", value: user.mobileNumber },
          {
            icon: <ShieldCheck />,
            label: "Role",
            value: user.role?.replace("ROLE_", ""),
          },
          { icon: <BadgeCheck />, label: "Status", value: user.userStatus },
          { icon: <Globe />, label: "Platform", value: user.platform },
          {
            icon: <MapPin />,
            label: "Address",
            value: (
              <div className="flex items-center justify-between">
                <span>{user.address?.fullAddress || "N/A"}</span>
                <button
                  onClick={() => {
                    setEditingAddress(user.address || null);
                    setIsAddressModalOpen(true);
                  }}
                  className="ml-2 text-sm text-primary hover:underline"
                >
                  {user.address?.fullAddress ? "Edit" : "Add"}
                </button>
              </div>
            ),
          },
        ].map((item) => (
          <ProfileField key={item.label} {...item} />
        ))}
      </div>

      {/* EcoCredits Section */}
      <div className="bg-white rounded-2xl p-6 shadow-inner flex flex-col md:flex-row items-center justify-between gap-4">
        <div className="space-y-2 text-center md:text-left">
          <h2 className="text-lg font-semibold">EcoCredits</h2>
          <p className="text-primary text-2xl font-bold">{eco}</p>
        </div>
        <div className="border-l border-gray-200 h-16 hidden md:block"></div>
        <div className="space-y-2 text-center md:text-left">
          <h2 className="text-lg font-semibold">Trees Planted</h2>
          <p className="text-primary text-2xl font-bold">{trees}</p>
          <p className="text-sm text-gray-500">
            {next} more credits for next tree
          </p>
        </div>
        <div className="flex flex-col md:flex-row gap-2">
          <button
            onClick={handleRedeemEcoCredits}
            className="bg-primary text-white px-6 py-2 rounded-full shadow hover:scale-105 transition disabled:opacity-60"
            disabled={isRedeeming}
          >
            {isRedeeming ? "Redeeming..." : "Redeem"}
          </button>

          <button
            onClick={handlePlantTree}
            className="bg-green-600 text-white px-6 py-2 rounded-full shadow hover:scale-105 transition disabled:opacity-60"
            disabled={isPlanting}
          >
            {isPlanting ? "Planting..." : "Plant a Tree"}
          </button>
        </div>
      </div>

      {/* 🌟 Next Milestone Section */}
      <div className="relative overflow-hidden rounded-2xl mt-6 p-5 bg-gradient-to-br from-primary.shade1 to-white shadow-glass border border-yellow-300 animate-fade-in">
        <div className="absolute top-0 right-0 m-2 bg-yellow-100 text-yellow-800 text-xxs px-2 py-1 rounded-full font-semibold uppercase shadow-sm flex items-center gap-1">
          <Award className="w-3.5 h-3.5" />
          Milestone
        </div>
        <div className="flex flex-col items-center justify-center text-center space-y-2">
          <h3 className="text-lg sm:text-xl font-bold text-text.highlight animate-float flex items-center gap-2">
            <Award className="w-5 h-5 text-primary.shade4" />
            Almost There!
          </h3>
          <p className="text-sm sm:text-base text-text.muted">
            Earn just{" "}
            <span className="font-bold text-primary">{nextMilestone}</span> more
            credits
          </p>
          <p className="text-sm text-gray-500 flex items-center justify-center gap-1">
            And unlock your next EcoCredits reward
            <Gift className="w-4 h-4 text-primary.shade2" />
          </p>
          <div className="mt-2">
            <button
              disabled
              className="flex items-center gap-2 px-4 py-1 text-xs sm:text-sm bg-yellow-400/20 text-yellow-700 font-semibold rounded-full shadow-inner cursor-not-allowed"
            >
              <Leaf className="w-4 h-4" />
              Keep Earning
            </button>
          </div>
        </div>
      </div>

      {/* Testimonials Section */}
      <UserTestimonials />

      {/* Earn More Credits */}
      <h4 className="mt-8 text-lg font-semibold text-primary mb-4">
        Earn More Credits
      </h4>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
        {/* Rent a Book */}
        <div className="bg-white rounded-xl p-4 text-center shadow-sm">
          <img
            src="/images/rent book.svg"
            alt="Rent a Book"
            className="mx-auto h-24"
          />
          <p className="mt-2 text-sm font-medium text-gray-700">
            Rent a Book : Earn 20 Credits
          </p>
        </div>

        {/* Refer a Friend */}
        <div className="bg-white rounded-xl p-4 text-center shadow-sm">
          <img
            src="/images/refer friend.svg"
            alt="Refer a Friend"
            className="mx-auto h-24"
          />
          <p className="mt-2 text-sm font-medium text-gray-700">
            Refer a Friend : Earn 50 Credits
          </p>

          {user.referralCode && (
            <div className="mt-3">
              <p className="text-xs text-gray-600">Your Referral Code:</p>
              <div className="mt-1 flex items-center justify-center gap-2">
                <span className="text-sm font-semibold bg-gray-100 px-3 py-1 rounded-md border border-gray-300">
                  {user.referralCode}
                </span>
                <button
                  onClick={() => {
                    navigator.clipboard.writeText(user.referralCode);
                    toast.success("Referral code copied!");
                  }}
                  className="text-xs bg-primary text-white px-2 py-1 rounded hover:bg-primary-dark transition"
                >
                  Copy
                </button>
              </div>

              <div className="mt-4 flex justify-center gap-4 flex-wrap text-sm">
                {/* WhatsApp */}
                <a
                  href={`https://wa.me/?text=${encodeURIComponent(
                    `Join BooksAtDoorsteps and earn credits! Use my referral code: ${user.referralCode} https://booksatdoorsteps.com`
                  )}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-1 text-green-600 hover:text-green-800"
                >
                  <FaWhatsapp size={18} />
                </a>

                {/* Instagram (cannot prefill message, just link to page) */}
                <a
                  href="https://www.instagram.com/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-1 text-pink-500 hover:text-pink-700"
                >
                  <FaInstagram size={18} />
                </a>

                {/* X (Twitter) */}
                <a
                  href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(
                    `Join BooksAtDoorsteps and get credits! Use my referral code: ${user.referralCode} https://booksatdoorsteps.com`
                  )}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-1 text-blue-500 hover:text-blue-700"
                >
                  <FaXTwitter size={18} />
                </a>

                {/* More - Web Share API */}
                {navigator.share && (
                  <button
                    onClick={() => {
                      navigator.share({
                        title: "BooksAtDoorsteps",
                        text: `Join BooksAtDoorsteps and get credits! Use my referral code: ${user.referralCode}`,
                        url: "https://booksatdoorsteps.com",
                      });
                    }}
                    className="flex items-center gap-1 text-gray-600 hover:text-black"
                  >
                    <FaShareAlt size={18} />
                    More
                  </button>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Leave a Review */}
        <div className="bg-white rounded-xl p-4 text-center shadow-sm">
          <img
            src="/images/leave review.svg"
            alt="Leave a Review"
            className="mx-auto h-24"
          />
          <p className="mt-2 text-sm font-medium text-gray-700">
            Leave a Review : Earn 10 Credits
          </p>
        </div>
      </div>

      {/* FAQ Accordion */}
      <h4 className="mt-10 mb-4 text-lg font-semibold text-primary">
        FAQ’s – EcoCredits
      </h4>
      <div className="space-y-2">
        <details className="bg-white rounded-lg p-4 shadow-sm">
          <summary className="cursor-pointer text-sm font-medium text-gray-800">
            What are EcoCredits?
          </summary>
          <p className="mt-2 text-gray-600 text-sm">
            EcoCredits are green reward points you earn by taking eco-friendly
            actions through BooksAtDoorsteps, such as renting books, referring
            friends, or leaving reviews.
          </p>
        </details>

        <details className="bg-white rounded-lg p-4 shadow-sm">
          <summary className="cursor-pointer text-sm font-medium text-gray-800">
            Why should I care about EcoCredits?
          </summary>
          <p className="mt-2 text-gray-600 text-sm">
            EcoCredits not only help you earn rewards (free rentals, discounts,
            etc.) but also contribute to planting real trees. Every 50 credits
            equals one “tree Planted.”
          </p>
        </details>

        <details className="bg-white rounded-lg p-4 shadow-sm">
          <summary className="cursor-pointer text-sm font-medium text-gray-800">
            How do I earn EcoCredits?
          </summary>
          <p className="mt-2 text-gray-600 text-sm">
            You can earn them by:
            <ul className="list-disc list-inside mt-1 text-gray-600 text-sm">
              <li>Renting a book (+20 credits)</li>
              <li>Referring a friend (+50 credits)</li>
              <li>Reviewing a rented book (+10 credits)</li>
              <li>Completing quizzes and surveys (coming soon)</li>
            </ul>
          </p>
        </details>

        <details className="bg-white rounded-lg p-4 shadow-sm">
          <summary className="cursor-pointer text-sm font-medium text-gray-800">
            How can I redeem my EcoCredits?
          </summary>
          <p className="mt-2 text-gray-600 text-sm">
            Once you have enough credits, click the “Redeem Credits” button at
            the top of this section to use them toward free rentals, discounts,
            or plant real trees. Stay tuned for more rewards!
          </p>
        </details>

        <details className="bg-white rounded-lg p-4 shadow-sm">
          <summary className="cursor-pointer text-sm font-medium text-gray-800">
            What can I do with EcoCredits?
          </summary>
          <p className="mt-2 text-gray-600 text-sm">
            You can redeem your EcoCredits for:
            <ul className="list-disc list-inside mt-1 text-gray-600 text-sm">
              <li>Discounts on your subscription plan</li>
              <li>Tree planting (500 credits = 1 native tree)</li>
            </ul>
          </p>
        </details>

        <details className="bg-white rounded-lg p-4 shadow-sm">
          <summary className="cursor-pointer text-sm font-medium text-gray-800">
            How does the tree planting work?
          </summary>
          <p className="mt-2 text-gray-600 text-sm">
            When you redeem 500 EcoCredits, we will either:
            <ul className="list-disc list-inside mt-1 text-gray-600 text-sm">
              <li>
                Deliver a sapling with your next book for you to plant, or
              </li>
              <li>Plant a tree on your behalf in a community/public place.</li>
            </ul>
            In both cases, we nurture the tree and share a photo + certificate
            on our social media with your name.
          </p>
        </details>

        <details className="bg-white rounded-lg p-4 shadow-sm">
          <summary className="cursor-pointer text-sm font-medium text-gray-800">
            Can I track my EcoCredits?
          </summary>
          <p className="mt-2 text-gray-600 text-sm">
            Yes! Just visit the "EcoCredit" section in your app dashboard to see
            your balance, earnings, redemptions, and environmental impact.
          </p>
        </details>

        <details className="bg-white rounded-lg p-4 shadow-sm">
          <summary className="cursor-pointer text-sm font-medium text-gray-800">
            Are EcoCredits transferable or refundable?
          </summary>
          <p className="mt-2 text-gray-600 text-sm">
            Currently, EcoCredits are non-transferable and non-refundable.
            However, they never expire and can be used anytime.
          </p>
        </details>

        <details className="bg-white rounded-lg p-4 shadow-sm">
          <summary className="cursor-pointer text-sm font-medium text-gray-800">
            Can I suggest new ways to earn EcoCredits?
          </summary>
          <p className="mt-2 text-gray-600 text-sm">
            Absolutely! We welcome community ideas. Drop your suggestions
            through our contact form or feedback section in the app.
          </p>
        </details>
      </div>

      {/* Logout */}
      <div className="text-center">
        <button
          onClick={handleLogout}
          className="flex items-center justify-center gap-2 text-red-500 font-semibold px-6 py-2 rounded-full border border-red-300 hover:bg-red-50 transition"
        >
          <LogOut /> Logout
        </button>
      </div>

      {/* Upload Modal */}
      <UploadPhotoModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onUpload={handleUpload}
      />
      {couponModal.visible && (
        <div className="fixed inset-0 z-50 bg-black bg-opacity-50 flex items-center justify-center">
          <div className="bg-white p-6 rounded-xl shadow-xl w-80 text-center space-y-4">
            <h3 className="text-lg font-semibold text-primary">
              🎉 Coupon Created!
            </h3>
            <p className="text-sm text-gray-700">
              Use this coupon on your next subscription:
            </p>
            <div className="flex justify-center items-center gap-2">
              <span className="bg-gray-100 px-4 py-2 rounded font-mono border text-sm">
                {couponModal.code}
              </span>
              <button
                onClick={() => {
                  navigator.clipboard.writeText(couponModal.code);
                  toast.success("Coupon code copied!");
                }}
                className="text-xs bg-primary text-white px-2 py-1 rounded hover:bg-primary-dark transition"
              >
                Copy
              </button>
            </div>
            <p className="text-xs text-gray-500">
              Valid till: {couponModal.expiry}
            </p>
            <button
              onClick={() => setCouponModal({ ...couponModal, visible: false })}
              className="mt-4 bg-gray-200 hover:bg-gray-300 text-gray-800 px-4 py-1 rounded text-sm"
            >
              Close
            </button>
          </div>
        </div>
      )}
      <AddAddressModal
        isOpen={isAddressModalOpen}
        onClose={() => setIsAddressModalOpen(false)}
        userId={user.userId}
        initialData={editingAddress}
        onSuccess={async () => {
          const updatedUser = await AuthManager.getUserDetails(
            user.userId,
            token
          );
          dispatch(updateUser(updatedUser.responseData));
        }}
      />
    </motion.div>
  );
};

// ---------- Subcomponents ----------
const ProfileField = ({ icon, label, value }) => (
  <motion.div
    className="bg-white p-4 rounded-xl flex items-center gap-4 shadow-sm hover:shadow-md transition"
    whileHover={{ translateY: -2 }}
  >
    <div className="p-2 bg-primary-shade1 rounded-lg text-primary">
      {React.cloneElement(icon, { size: 20 })}
    </div>
    <div className="flex-1">
      <p className="text-sm text-gray-600">{label}</p>
      <p className="font-medium text-gray-800">{value}</p>
    </div>
  </motion.div>
);

const IconAction = ({ icon, label, onClick }) => (
  <motion.button
    onClick={onClick}
    className="flex items-center gap-1 bg-white/90 backdrop-blur-sm px-4 py-2 rounded-full shadow hover:shadow-lg transition"
    whileHover={{ scale: 1.05 }}
  >
    {React.cloneElement(icon, { className: "text-primary" })}
    <span className="text-sm font-medium text-gray-800">{label}</span>
  </motion.button>
);

export default UserProfile;
